import React, { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ScanLine, Ticket, CheckCircle2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import QRScannerModal from "./QRScannerModal";

export default function RaffleTicketAward({ business }) {
  const [scannerOpen, setScannerOpen] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupErr, setLookupErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastAwarded, setLastAwarded] = useState(null);
  const lookupTimer = useRef(null);

  // Auto-lookup when phone reaches 10 digits
  useEffect(() => {
    setLookup(null); setLookupErr("");
    const digits = whatsapp.replace(/\D/g, "");
    if (digits.length < 10) return;
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/business/loyalty/customer-lookup?whatsapp=${digits}`);
        setLookup(data); setLookupErr("");
      } catch (e) {
        setLookup(null);
        setLookupErr(e.response?.data?.detail || "Cliente no registrado");
      }
    }, 400);
    return () => lookupTimer.current && clearTimeout(lookupTimer.current);
  }, [whatsapp]);

  const award = async (phone) => {
    const digits = (phone || whatsapp).replace(/\D/g, "");
    if (digits.length < 10) return toast.error("Celular inválido");
    setBusy(true);
    try {
      const { data } = await api.post("/business/raffle/award-ticket", { customer_whatsapp: digits });
      setLastAwarded(data);
      setWhatsapp(""); setLookup(null);
      toast.success(`¡Boleto otorgado a ${data.customer_name || digits}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al otorgar boleto");
    } finally { setBusy(false); }
  };

  const handleScan = (digits) => {
    setScannerOpen(false);
    setWhatsapp(digits);
    toast.success("QR escaneado — otorgando boleto…");
    // Auto-award immediately after scan
    award(digits);
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="raffle-ticket-award">
      <div className="flex items-center gap-2 mb-1">
        <Ticket className="w-5 h-5 text-[#E05A47]" />
        <h3 className="font-display text-2xl tracking-tight">Dar boleto de rifa</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Escanea el QR del cliente o escribe su celular. El boleto se otorga automáticamente.
      </p>

      {/* Primary CTA: scan */}
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <Button
          type="button"
          onClick={() => setScannerOpen(true)}
          className="btn-primary rounded-full px-6 py-5 text-base"
          data-testid="raffle-scan-btn"
          disabled={busy}
        >
          <ScanLine className="w-5 h-5 mr-2" />
          {busy ? "Otorgando…" : "Escanear QR del cliente"}
        </Button>
      </div>

      {/* Manual phone fallback */}
      <div className="mt-5">
        <Label className="text-xs text-muted-foreground">O escribe el celular manualmente</Label>
        <div className="flex gap-2 mt-1.5">
          <Input
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value.replace(/\D/g, "").slice(0, 12))}
            inputMode="numeric"
            placeholder="5512345678"
            className="max-w-[200px]"
            data-testid="raffle-phone-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => award()}
            disabled={busy || !lookup}
            data-testid="raffle-award-manual-btn"
          >
            {busy ? "…" : "Otorgar"}
          </Button>
        </div>

        {lookup && (
          <div className="mt-3 bg-gradient-to-r from-[#FEF3F2] to-[#FFF1EE] border border-[#FECDCA] rounded-xl p-4 flex items-center gap-3" data-testid="raffle-customer-preview">
            <UserCheck className="w-5 h-5 text-[#E05A47] shrink-0" />
            <div>
              <div className="text-xs text-muted-foreground">Cliente encontrado</div>
              <div className="font-display text-lg leading-tight">{lookup.customer_name}</div>
              <div className="text-xs text-muted-foreground font-mono mt-0.5">{lookup.customer_whatsapp}</div>
            </div>
          </div>
        )}
        {lookupErr && whatsapp.replace(/\D/g, "").length >= 10 && (
          <div className="mt-2 text-sm text-rose-600" data-testid="raffle-lookup-err">{lookupErr}</div>
        )}
      </div>

      {/* Last awarded confirmation */}
      {lastAwarded && (
        <div className="mt-5 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="raffle-last-awarded">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-emerald-800">¡Boleto otorgado!</div>
            <div className="text-xs text-emerald-700 mt-0.5">
              {lastAwarded.customer_name} · {lastAwarded.month}
            </div>
          </div>
        </div>
      )}

      <QRScannerModal open={scannerOpen} onClose={() => setScannerOpen(false)} onScanned={handleScan} />
    </div>
  );
}
